import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { PLATFORMS, STATUS_META } from "../lib/meta";
import { Explainer } from "../components/friendly";

const PLAN_STATUSES = ["draft", "pending_approval", "changes_requested"];
const READY_STATUSES = ["approved", "posted"];

export default function CalendarPage() {
  const { activeClientId, activeClient } = useApp();
  const [month, setMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [drafts, setDrafts] = useState([]);
  const [brand, setBrand] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    if (!activeClientId) return;
    api.get(`/drafts?client_id=${activeClientId}`).then((r) => setDrafts(r.data));
    api.get(`/clients/${activeClientId}/brand`).then((r) => setBrand(r.data)).catch(() => setBrand(null));
  }, [activeClientId]);
  useEffect(load, [load]);

  const first = new Date(month.y, month.m, 1);
  const startDay = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(month.y, month.m + 1, 0).getDate();
  const cells = [...Array(startDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const dateStr = (day) => `${month.y}-${String(month.m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const byDate = (day) => drafts.filter((d) => d.scheduled_date === dateStr(day));
  const monthLabel = first.toLocaleString("en", { month: "long", year: "numeric" });
  const shift = (dir) => setMonth(({ y, m }) => { const d = new Date(y, m + dir, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const today = new Date();
  const isToday = (day) => today.getFullYear() === month.y && today.getMonth() === month.m && today.getDate() === day;

  const monthPrefix = `${month.y}-${String(month.m + 1).padStart(2, "0")}`;
  const monthDrafts = drafts.filter((d) => d.scheduled_date?.startsWith(monthPrefix));
  const weeksInMonth = daysInMonth / 7;
  const cadence = Object.keys(PLATFORMS).map((p) => {
    const target = Math.round((brand?.target_cadence?.[p] || 0) * weeksInMonth);
    const scheduled = monthDrafts.filter((d) => d.platform === p).length;
    return { p, target, scheduled };
  }).filter((c) => c.target > 0 || c.scheduled > 0);
  const totalMix = monthDrafts.length;

  const Chip = ({ d }) => (
    <div data-testid={`calendar-draft-${d.draft_id}`} title={d.caption}
      className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] truncate border" style={{ borderColor: "#e6e6ee", background: "#fafafc" }}>
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: PLATFORMS[d.platform]?.color || "#999" }} />
      <span className="truncate text-slate-600">{STATUS_META[d.status]?.label}</span>
    </div>
  );

  return (
    <div className="space-y-6 fade-up" data-testid="calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Calendar</h1>
          <p className="text-sm text-slate-500 mt-1">Two lanes per day for <b>{activeClient?.name}</b>: <span className="text-amber-600 font-medium">Plan</span> and <span className="text-emerald-600 font-medium">Ready</span>.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="calendar-prev" onClick={() => shift(-1)} className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"><CaretLeft size={14} /></button>
          <span className="font-display font-bold text-base w-44 text-center">{monthLabel}</span>
          <button data-testid="calendar-next" onClick={() => shift(1)} className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"><CaretRight size={14} /></button>
        </div>
      </div>
      <Explainer id="calendar">Your month at a glance for the selected brand. Amber chips are posts still being worked on; green chips are approved and ready. Use it to spot empty weeks before they happen.</Explainer>

      <div className="bg-white border border-slate-200/80 rounded-xl card-float overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-100">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="px-2 py-2 text-[11px] uppercase tracking-widest text-slate-400 font-medium text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((day, i) => (
            <div key={i} className={`min-h-[104px] border-r border-b border-slate-100 p-1.5 ${day ? "cursor-pointer hover:bg-slate-50/60 transition-colors" : "bg-slate-50/40"}`}
              onClick={() => day && navigate("/composer")} data-testid={day ? `calendar-day-${day}` : undefined}>
              {day && (
                <>
                  <span className={`inline-flex w-6 h-6 items-center justify-center rounded-full text-[12px] font-semibold ${isToday(day) ? "bg-[hsl(9,69%,62%)] text-white" : "text-slate-500"}`}>{day}</span>
                  <div className="mt-1 space-y-0.5">
                    {byDate(day).filter((d) => PLAN_STATUSES.includes(d.status)).slice(0, 2).map((d) => <Chip key={d.draft_id} d={d} />)}
                    {byDate(day).filter((d) => READY_STATUSES.includes(d.status)).slice(0, 2).map((d) => <Chip key={d.draft_id} d={d} />)}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-4 text-[12px] text-slate-500">
        {Object.entries(PLATFORMS).map(([k, p]) => <span key={k} className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: p.color }} />{p.label}</span>)}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200/80 rounded-xl p-5 card-float" data-testid="cadence-tracker">
          <h2 className="font-display font-bold text-base">Celebre tracker</h2>
          <p className="text-[11px] text-slate-400 mb-4">Scheduled this month vs. target (set in Brand Intelligence)</p>
          {cadence.length ? cadence.map(({ p, target, scheduled }) => (
            <div key={p} className="mb-3">
              <div className="flex justify-between text-[12px] mb-1">
                <span className="font-medium text-slate-600">{PLATFORMS[p].label}</span>
                <span className={scheduled >= target ? "text-emerald-600 font-semibold" : "text-slate-400"}>{scheduled} / {target || "—"}</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100">
                <div className={`h-full rounded-full ${scheduled >= target && target > 0 ? "bg-emerald-500" : "bg-[hsl(9,69%,62%)]"}`}
                  style={{ width: `${target ? Math.min(100, (scheduled / target) * 100) : scheduled > 0 ? 100 : 0}%` }} />
              </div>
            </div>
          )) : <p className="text-[13px] text-slate-400">Set a target cadence in Brand Intelligence to track posting pace.</p>}
        </div>
        <div className="bg-white border border-slate-200/80 rounded-xl p-5 card-float" data-testid="content-mix">
          <h2 className="font-display font-bold text-base">Content mix</h2>
          <p className="text-[11px] text-slate-400 mb-4">Share of this month's {totalMix} scheduled post{totalMix === 1 ? "" : "s"} per platform</p>
          {totalMix ? (
            <>
              <div className="flex h-3 rounded-full overflow-hidden">
                {Object.keys(PLATFORMS).map((p) => {
                  const n = monthDrafts.filter((d) => d.platform === p).length;
                  return n ? <div key={p} style={{ width: `${(n / totalMix) * 100}%`, background: PLATFORMS[p].color }} title={`${PLATFORMS[p].label}: ${n}`} /> : null;
                })}
              </div>
              <div className="mt-3 flex flex-wrap gap-3">
                {Object.keys(PLATFORMS).map((p) => {
                  const n = monthDrafts.filter((d) => d.platform === p).length;
                  return n ? <span key={p} className="text-[12px] text-slate-500 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: PLATFORMS[p].color }} />{PLATFORMS[p].label} {Math.round((n / totalMix) * 100)}%</span> : null;
                })}
              </div>
              {cadence.some((c) => c.target > 0 && c.scheduled < c.target) && (
                <p className="mt-4 text-[12px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  Under target: {cadence.filter((c) => c.target > 0 && c.scheduled < c.target).map((c) => `${PLATFORMS[c.p].label} (−${c.target - c.scheduled})`).join(", ")} — balance the mix in the Composer.
                </p>
              )}
            </>
          ) : <p className="text-[13px] text-slate-400">Nothing scheduled this month yet.</p>}
        </div>
      </div>
    </div>
  );
}
